// Sugalarm — cycle 316: the first-run onboarding tour's tiny state seam.
//
// One module-level store (the alarmPreview/clockMode pattern) so any
// surface can LAUNCH the tour — the first-run trigger in App.tsx and the
// "Run setup again" entry on the Alarm Reliability screen — while the tour
// overlay itself mounts exactly once at the App level.
//
// The once-per-device flag is DEVICE-LOCAL (localStorage): completing OR
// skipping the tour stamps it, so the tour never auto-reappears; the
// cycle-315 home banner + Alarm Reliability screen stay the persistent nag
// path for anything still unmet. Nothing here syncs through JSONB settings
// or `settings_update` frames (per-device UI state, not account state).

const DONE_KEY = "sugalarm.alarmTourDone";

let open = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function subscribeAlarmTour(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function isAlarmTourOpen(): boolean {
  return open;
}

/** Open the tour overlay (used by the first-run trigger and the "Run setup
 *  again" entry — a relaunch works whether or not the done flag is set). */
export function launchAlarmTour(): void {
  if (open) return;
  open = true;
  emit();
}

/** Close the overlay. `markDone` stamps the once-per-device flag (any
 *  terminal exit — finished, "Set up later", "Continue anyway"). */
export function closeAlarmTour(markDone: boolean): void {
  if (markDone) markAlarmTourDone();
  if (!open) return;
  open = false;
  emit();
}

export function isAlarmTourDone(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === "1";
  } catch {
    // No storage → treat as done so a broken store can never loop the tour.
    return true;
  }
}

export function markAlarmTourDone(): void {
  try {
    localStorage.setItem(DONE_KEY, "1");
  } catch {
    /* storage unavailable — the in-memory close still ends the tour */
  }
}
